import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { X, RotateCcw, Printer, FileDown, FileText, Pencil, Check, Trash2 } from 'lucide-react';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, WidthType, AlignmentType, BorderStyle, HeadingLevel, ShadingType } from 'docx';
import { saveAs } from 'file-saver';
import api from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const enconOrange = '#ee7203';

const formatDisplayDate = (iso) => {
  if (!iso) return '\u2014';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`;
};

const CORE_QUESTIONS = [
  "Can you talk me through your current role at CCF and the type of customers you deal with day to day?",
  "What do you feel are the key skills needed to be successful in an internal sales role?",
  "This role will involve dealing with HVAC and OME products. What exposure have you had to technical product sales, and how comfortable are you learning new product ranges quickly?",
  "Can you give an example of how you have turned an enquiry or quote into an order?",
  "How do you approach building relationships with contractors, buyers, or procurement teams over time?",
  "Tell me about a time when you had to deal with a difficult customer or complaint. What did you do and what was the outcome?",
  "How do you make sure quotes, pricing, and order details are accurate when things are busy?",
  "What is your approach to following up quotations without being too pushy?",
  "Have you had to manage delivery expectations, stock issues, or lead times with customers? How did you handle it?",
  "In this type of role, there can be pressure to protect margin as well as win business. How do you balance being competitive with maintaining profitability?",
  "What systems have you used for sales order processing, customer records, quotes, or pipeline management?",
  "Your CV shows you've worked across sales, customer service, and more hands-on roles. How do you think that mix of experience helps you in an internal sales position?",
  "What attracts you to this role specifically, and why are you looking to move from CCF?",
  "How do you prioritise your workload when you have multiple enquiries, call-backs, quote requests, and customer issues all at once?",
  "Can you give an example of where you worked closely with other departments, such as transport, supply chain, or external sales, to get the right outcome for a customer?",
  "What do you think good customer service looks like in a busy technical sales environment?",
  "How confident are you on the phone with existing customers, lapsed accounts, and new opportunities?",
  "Where do you see yourself developing over the next couple of years, and what would success look like for you in this role?"
];

const HVAC_QUESTIONS = [
  "What do you know about the HVAC market and the types of customers or applications involved?",
  "If a customer asked for a product you weren't familiar with, how would you go about finding the right answer quickly and confidently?",
  "How would you handle a situation where a customer needs something urgently, but stock or lead time is an issue?"
];

const CLOSING_QUESTION = "Is there anything in your background that makes you particularly well suited to a technical internal sales role that we haven't covered yet?";

const ALL_QUESTIONS = [...CORE_QUESTIONS, ...HVAC_QUESTIONS, CLOSING_QUESTION];

const SCORING_GUIDE = [
  { score: 1, label: "Weak / vague" },
  { score: 2, label: "Okay but limited" },
  { score: 3, label: "Solid" },
  { score: 4, label: "Very good" },
  { score: 5, label: "Excellent / proven example" }
];

const InterviewNotesModal = ({ isOpen, onClose, initialData, user }) => {
  const [candidateName, setCandidateName] = useState(initialData?.candidate_name || '');
  const [interviewDate, setInterviewDate] = useState(initialData?.date || new Date().toISOString().split('T')[0]);
  const [interviewer, setInterviewer] = useState(initialData?.interviewer || user?.name || user?.username || '');
  const [questions, setQuestions] = useState(() => {
    if (initialData?.questions) return initialData.questions;
    const initial = {};
    ALL_QUESTIONS.forEach((_, i) => { initial[i + 1] = { notes: '', score: 0 }; });
    return initial;
  });
  const [summary, setSummary] = useState(initialData?.summary || {
    overall_impression: '',
    key_strengths: '',
    areas_of_concern: '',
    recommendation: ''
  });
  const [customQuestions, setCustomQuestions] = useState(() => {
    if (initialData?.custom_questions) return initialData.custom_questions;
    const cq = {};
    ALL_QUESTIONS.forEach((q, i) => { cq[i + 1] = q; });
    return cq;
  });
  // True when the question list was AI-tailored to a CV (variable length,
  // distinct from the default 22 CORE+HVAC+CLOSING set). Also true when the
  // payload has a `cv_generated` flag.
  const [isCvMode, setIsCvMode] = useState(!!initialData?.cv_generated);
  // ID of the persisted note in MongoDB. Created on first autosave; reused
  // for every subsequent autosave so we don't spam new rows.
  const [noteId, setNoteId] = useState(initialData?.id || null);
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // idle | saving | saved | error

  // Sync state to initialData every time the modal is opened with new data.
  // Without this, useState's lazy initializer only runs on first mount and
  // subsequent opens (e.g. switching from "New Notes" to "New Notes from CV")
  // would keep the previous candidate's data and questions.
  useEffect(() => {
    if (!isOpen) return;
    setCandidateName(initialData?.candidate_name || '');
    setInterviewDate(initialData?.date || new Date().toISOString().split('T')[0]);
    setInterviewer(initialData?.interviewer || user?.name || user?.username || '');
    if (initialData?.questions) {
      setQuestions(initialData.questions);
    } else {
      const fresh = {};
      ALL_QUESTIONS.forEach((_, i) => { fresh[i + 1] = { notes: '', score: 0 }; });
      setQuestions(fresh);
    }
    setSummary(initialData?.summary || {
      overall_impression: '',
      key_strengths: '',
      areas_of_concern: '',
      recommendation: '',
    });
    if (initialData?.custom_questions) {
      setCustomQuestions(initialData.custom_questions);
    } else {
      const cq = {};
      ALL_QUESTIONS.forEach((q, i) => { cq[i + 1] = q; });
      setCustomQuestions(cq);
    }
    setIsCvMode(!!initialData?.cv_generated);
    setNoteId(initialData?.id || null);
    setAutosaveStatus('idle');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  // ---------------------------------------------------------------------
  // Autosave: debounced 1.2s after any edit. POST creates the row first
  // time, then PUT updates it. Saves to MongoDB so notes survive browser
  // refreshes and can be reopened from "Saved Notes".
  const noteIdRef = useRef(null);
  noteIdRef.current = noteId;
  const saveTimerRef = useRef(null);
  const lastSerialisedRef = useRef('');
  const persist = useCallback(async () => {
    if (!isOpen) return;
    // Skip persist when nothing has been entered yet — avoids creating empty
    // rows just because the modal was opened.
    const hasContent = !!(candidateName || interviewer
      || Object.values(questions).some(q => (q?.notes || '').trim() || q?.score)
      || Object.values(summary).some(v => (v || '').trim()));
    if (!hasContent) return;
    const payload = {
      candidate_name: candidateName,
      date: interviewDate,
      interviewer,
      questions,
      summary,
      custom_questions: isCvMode ? customQuestions : null,
    };
    const serialised = JSON.stringify(payload);
    if (serialised === lastSerialisedRef.current) return;  // nothing changed
    lastSerialisedRef.current = serialised;
    setAutosaveStatus('saving');
    try {
      if (noteIdRef.current) {
        await api.put(`${API}/interview-notes/${noteIdRef.current}`, payload);
      } else {
        const res = await api.post(`${API}/interview-notes`, payload);
        if (res.data?.id) {
          setNoteId(res.data.id);
          noteIdRef.current = res.data.id;
        }
      }
      setAutosaveStatus('saved');
    } catch (e) {
      console.warn('Interview note autosave failed:', e);
      setAutosaveStatus('error');
    }
  }, [isOpen, candidateName, interviewDate, interviewer, questions, summary, customQuestions, isCvMode]);

  useEffect(() => {
    if (!isOpen) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { persist(); }, 1200);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [isOpen, persist]);

  // Active question keys (1..N as strings). For CV mode we use whatever the
  // payload returned; for default mode we walk the canonical 1..22 sequence.
  const cvQuestionKeys = isCvMode
    ? Object.keys(customQuestions).map(Number).sort((a, b) => a - b)
    : [];
  const cvQuestionList = cvQuestionKeys.map(k => customQuestions[k]);

  const updateQuestionText = (num, text) => {
    setCustomQuestions(prev => ({ ...prev, [num]: text }));
  };

  const getQuestion = (num) => customQuestions[num] || ALL_QUESTIONS[num - 1] || '';

  const updateQuestion = (num, field, value) => {
    setQuestions(prev => ({ ...prev, [num]: { ...prev[num], [field]: value } }));
  };

  const resetForm = () => {
    if (!window.confirm('Reset all fields? This cannot be undone.')) return;
    setCandidateName('');
    setInterviewDate(new Date().toISOString().split('T')[0]);
    setInterviewer('');
    setQuestions(() => {
      const q = {};
      ALL_QUESTIONS.forEach((_, i) => { q[i + 1] = { notes: '', score: 0 }; });
      return q;
    });
    setSummary({ overall_impression: '', key_strengths: '', areas_of_concern: '', recommendation: '' });
    const cq = {};
    ALL_QUESTIONS.forEach((q, i) => { cq[i + 1] = q; });
    setCustomQuestions(cq);
  };

  // ----- CV-mode list mutations: reorder / add / delete --------------------
  // All three operate on the parallel `customQuestions` (text) and
  // `questions` ({notes, score}) dicts, keeping their integer keys
  // contiguous from 1..N.

  const _rebuildFromOrdered = (ordered) => {
    // ordered = [{ text, data }, ...] in the new desired order.
    const newCq = {};
    const newQ = {};
    ordered.forEach((row, idx) => {
      const num = idx + 1;
      newCq[num] = row.text || '';
      newQ[num] = row.data || { notes: '', score: 0 };
    });
    setCustomQuestions(newCq);
    setQuestions(newQ);
  };

  const moveQuestion = (fromNum, toNum) => {
    if (!isCvMode) return;
    const total = cvQuestionKeys.length;
    const tgt = Math.max(1, Math.min(total, parseInt(toNum, 10) || 0));
    if (tgt === fromNum) return;
    const ordered = cvQuestionKeys.map(k => ({
      text: customQuestions[k] || '',
      data: questions[k] || { notes: '', score: 0 },
    }));
    const [picked] = ordered.splice(fromNum - 1, 1);
    ordered.splice(tgt - 1, 0, picked);
    _rebuildFromOrdered(ordered);
  };

  const addQuestion = (afterNum = null) => {
    if (!isCvMode) return;
    const ordered = cvQuestionKeys.map(k => ({
      text: customQuestions[k] || '',
      data: questions[k] || { notes: '', score: 0 },
    }));
    const insertAt = (afterNum && afterNum >= 1 && afterNum <= ordered.length) ? afterNum : ordered.length;
    ordered.splice(insertAt, 0, { text: '', data: { notes: '', score: 0 } });
    _rebuildFromOrdered(ordered);
  };

  const deleteQuestion = (num) => {
    if (!isCvMode) return;
    if (cvQuestionKeys.length <= 1) {
      toast.error('At least one question is required');
      return;
    }
    if (!window.confirm('Delete this question?')) return;
    const ordered = cvQuestionKeys
      .filter(k => k !== num)
      .map(k => ({
        text: customQuestions[k] || '',
        data: questions[k] || { notes: '', score: 0 },
      }));
    _rebuildFromOrdered(ordered);
  };

  const totalScore = Object.values(questions).reduce((sum, q) => sum + (q.score || 0), 0);
  const questionsScored = Object.values(questions).filter(q => q.score > 0).length;
  const activeQuestionCount = isCvMode ? cvQuestionKeys.length : ALL_QUESTIONS.length;
  const maxScore = activeQuestionCount * 5;

  // ============ PDF Generation ============
  const generatePDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = 15;

    const addPageIfNeeded = (needed) => {
      if (y + needed > 275) { doc.addPage(); y = 15; }
    };

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(238, 114, 3);
    doc.text('Technical Internal Sales', margin, y);
    y += 7;
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('First Interview Questions', margin, y);
    y += 10;

    doc.setFillColor(238, 114, 3);
    doc.rect(margin, y, contentW, 8, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(`CANDIDATE: ${candidateName || '\u2014'}`, margin + 3, y + 5.5);
    doc.text(`DATE: ${formatDisplayDate(interviewDate)}`, margin + contentW * 0.4, y + 5.5);
    doc.text(`INTERVIEWER: ${interviewer || '\u2014'}`, margin + contentW * 0.7, y + 5.5);
    y += 12;

    doc.setFillColor(245, 245, 245);
    doc.rect(margin, y, contentW, 7, 'F');
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('1 = Weak/vague    2 = Okay but limited    3 = Solid    4 = Very good    5 = Excellent/proven example', margin + 3, y + 4.5);
    y += 11;

    const renderSection = (title, qList, startNum) => {
      addPageIfNeeded(12);
      doc.setFillColor(238, 114, 3);
      doc.rect(margin, y, contentW, 7, 'F');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin + 3, y + 5);
      y += 10;

      qList.forEach((_, idx) => {
        const num = startNum + idx;
        const question = getQuestion(num);
        const notes = questions[num]?.notes || '';
        const score = questions[num]?.score || 0;

        // Question text — full width, slightly larger font
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const qLines = doc.splitTextToSize(question, contentW - 10);
        const qH = qLines.length * 5 + 6;

        // Notes area dimensions
        const notesW = contentW - 52;
        doc.setFontSize(8);
        const nLines = notes ? doc.splitTextToSize(notes, notesW - 6) : [];
        const notesH = Math.max(nLines.length * 4 + 6, 16);

        const totalH = qH + notesH + 2;
        addPageIfNeeded(totalH + 2);

        // Row background
        doc.setFillColor(idx % 2 === 0 ? 255 : 248, idx % 2 === 0 ? 255 : 248, idx % 2 === 0 ? 255 : 248);
        doc.rect(margin, y, contentW, totalH, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.rect(margin, y, contentW, totalH, 'S');

        // Question number (orange bold)
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(238, 114, 3);
        doc.text(`${num}.`, margin + 2, y + 5);

        // Question text — full width
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(40, 40, 40);
        doc.text(qLines, margin + 10, y + 5);

        // Score + Notes row below question
        const scoreRowY = y + qH;

        // "Score:" label
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(150, 150, 150);
        doc.text('Score:', margin + 2, scoreRowY + notesH / 2 + 1);

        // 5 score circles on the left
        for (let s = 1; s <= 5; s++) {
          const cx = margin + 18 + (s - 1) * 6.5;
          const cy = scoreRowY + notesH / 2;
          if (s === score) {
            doc.setFillColor(238, 114, 3);
            doc.circle(cx, cy, 2.8, 'F');
            doc.setTextColor(255, 255, 255);
          } else {
            doc.setDrawColor(180, 180, 180);
            doc.circle(cx, cy, 2.8, 'S');
            doc.setTextColor(150, 150, 150);
          }
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.text(String(s), cx - 1.2, cy + 1.2);
        }

        // Notes oblong box — right side, taking remaining width
        const notesX = margin + 52;
        doc.setFillColor(250, 250, 250);
        doc.setDrawColor(200, 200, 200);
        doc.rect(notesX, scoreRowY + 2, notesW, notesH - 4, 'FD');
        if (nLines.length > 0) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60, 60, 60);
          doc.text(nLines, notesX + 3, scoreRowY + 6);
        }

        y += totalH;
      });
    };

    if (isCvMode) {
      renderSection('Interview Questions (tailored to CV)', cvQuestionList, 1);
      y += 8;
    } else {
      renderSection('Core Interview Questions', CORE_QUESTIONS, 1);
      y += 4;
      renderSection('Additional HVAC / OME Questions', HVAC_QUESTIONS, 19);
      y += 4;
      renderSection('Closing Question', [CLOSING_QUESTION], 22);
      y += 8;
    }

    addPageIfNeeded(60);
    doc.setFillColor(238, 114, 3);
    doc.rect(margin, y, contentW, 7, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('Interview Summary', margin + 3, y + 5);
    y += 10;

    [
      { label: 'OVERALL IMPRESSION', value: summary.overall_impression },
      { label: 'KEY STRENGTHS', value: summary.key_strengths },
      { label: 'AREAS OF CONCERN', value: summary.areas_of_concern },
      { label: 'RECOMMENDATION', value: summary.recommendation }
    ].forEach(field => {
      addPageIfNeeded(20);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(238, 114, 3);
      doc.text(field.label, margin, y + 4);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const lines = doc.splitTextToSize(field.value || '\u2014', contentW - 4);
      const boxH = Math.max(lines.length * 4 + 4, 10);
      doc.setFillColor(250, 250, 250);
      doc.rect(margin, y, contentW, boxH, 'F');
      doc.setDrawColor(220, 220, 220);
      doc.rect(margin, y, contentW, boxH, 'S');
      doc.text(lines, margin + 2, y + 4);
      y += boxH + 4;
    });

    addPageIfNeeded(16);
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, y, contentW, 12, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(238, 114, 3);
    doc.text(`TOTAL SCORE: ${totalScore} / ${maxScore}`, margin + 4, y + 5);
    doc.setTextColor(100, 100, 100);
    doc.text(`QUESTIONS SCORED: ${questionsScored} / ${activeQuestionCount}`, margin + 4, y + 10);

    // Embed the full interview state as base64 JSON in the PDF Subject
    // metadata. The Edit-Notes upload endpoint reads this back so the
    // roundtrip is loss-less — no fragile text scraping when a saved PDF is
    // re-uploaded into the modal.
    try {
      const payload = {
        v: 1,
        cv_generated: !!isCvMode,
        candidate_name: candidateName,
        date: interviewDate,
        interviewer,
        questions,
        custom_questions: customQuestions,
        summary,
      };
      const json = JSON.stringify(payload);
      const b64 = typeof window !== 'undefined' && window.btoa
        ? window.btoa(unescape(encodeURIComponent(json)))
        : Buffer.from(json, 'utf-8').toString('base64');
      doc.setProperties({
        title: `Interview Notes - ${candidateName || 'Candidate'}`,
        subject: `INTERVIEW_NOTES_JSON_B64:${b64}`,
        keywords: 'encon interview notes',
      });
    } catch (_) { /* metadata is best-effort, never block the download */ }

    const fileName = candidateName
      ? `Interview_Notes_${candidateName.replace(/\s+/g, '_')}.pdf`
      : 'Interview_Notes.pdf';
    doc.save(fileName);
    toast.success('PDF downloaded');
  };

  // ============ Word (.docx) Generation ============
  const generateWord = async () => {
    try {
      const orangeHex = 'EE7203';
      const borderNone = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
      const noBorders = { top: borderNone, bottom: borderNone, left: borderNone, right: borderNone };
      const thinBorder = { style: BorderStyle.SINGLE, size: 1, color: 'DDDDDD' };
      const cellBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

      const children = [];

      // Title
      children.push(new Paragraph({
        children: [new TextRun({ text: 'Technical Internal Sales', bold: true, size: 32, color: orangeHex, font: 'Calibri' })],
        spacing: { after: 80 }
      }));
      children.push(new Paragraph({
        children: [new TextRun({ text: 'First Interview Questions', size: 24, color: '666666', font: 'Calibri' })],
        spacing: { after: 200 }
      }));

      // Candidate info row
      children.push(new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: `CANDIDATE: ${candidateName || '\u2014'}`, bold: true, size: 18, color: 'FFFFFF', font: 'Calibri' })] })],
                width: { size: 40, type: WidthType.PERCENTAGE },
                shading: { type: ShadingType.SOLID, color: orangeHex },
                borders: noBorders
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: `DATE: ${formatDisplayDate(interviewDate)}`, bold: true, size: 18, color: 'FFFFFF', font: 'Calibri' })] })],
                width: { size: 30, type: WidthType.PERCENTAGE },
                shading: { type: ShadingType.SOLID, color: orangeHex },
                borders: noBorders
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: `INTERVIEWER: ${interviewer || '\u2014'}`, bold: true, size: 18, color: 'FFFFFF', font: 'Calibri' })] })],
                width: { size: 30, type: WidthType.PERCENTAGE },
                shading: { type: ShadingType.SOLID, color: orangeHex },
                borders: noBorders
              }),
            ]
          })
        ]
      }));

      // Scoring guide
      children.push(new Paragraph({ spacing: { before: 120, after: 120 }, children: [
        new TextRun({ text: '1 = Weak/vague    2 = Okay but limited    3 = Solid    4 = Very good    5 = Excellent/proven example', size: 16, color: '666666', font: 'Calibri', italics: true })
      ]}));

      // Question sections
      const buildSection = (title, qList, startNum) => {
        // Section header
        children.push(new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new TableRow({
            children: [new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: title, bold: true, size: 18, color: 'FFFFFF', font: 'Calibri' })] })],
              shading: { type: ShadingType.SOLID, color: orangeHex },
              borders: noBorders,
              columnSpan: 2
            })]
          })]
        }));

        // Question rows — new layout: question full-width, then score (left) + notes oblong (right)
        const rows = [];
        qList.forEach((_, idx) => {
          const num = startNum + idx;
          const question = getQuestion(num);
          const notes = questions[num]?.notes || '';
          const score = questions[num]?.score || 0;
          const bgColor = idx % 2 === 0 ? 'FFFFFF' : 'FAFAFA';

          // Row 1: Full-width question text (spans both columns)
          rows.push(new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({
                  spacing: { before: idx === 0 ? 60 : 120, after: 60 },
                  children: [
                    new TextRun({ text: `${num}. `, bold: true, size: 18, color: orangeHex, font: 'Calibri' }),
                    new TextRun({ text: question, size: 18, color: '333333', font: 'Calibri' })
                  ]
                })],
                columnSpan: 2,
                borders: {
                  top: idx === 0 ? borderNone : thinBorder,
                  bottom: borderNone,
                  left: borderNone,
                  right: borderNone
                },
                shading: { type: ShadingType.SOLID, color: bgColor }
              })
            ]
          }));

          // Row 2: Score (narrow, left) + Notes oblong (wide, right)
          rows.push(new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({
                  spacing: { before: 60, after: 120 },
                  children: [new TextRun({ text: score > 0 ? `Score: ${score} / 5` : 'Score: \u2014', bold: true, size: 18, color: score > 0 ? orangeHex : '999999', font: 'Calibri' })]
                })],
                width: { size: 20, type: WidthType.PERCENTAGE },
                borders: { top: borderNone, bottom: thinBorder, left: borderNone, right: thinBorder },
                shading: { type: ShadingType.SOLID, color: bgColor }
              }),
              new TableCell({
                children: [
                  new Paragraph({ spacing: { before: 40 }, children: [new TextRun({ text: notes || '', size: 16, color: '555555', font: 'Calibri' })] }),
                  new Paragraph({ children: [] }),
                  new Paragraph({ children: [] }),
                ],
                width: { size: 80, type: WidthType.PERCENTAGE },
                borders: { top: borderNone, bottom: thinBorder, left: borderNone, right: borderNone },
                shading: { type: ShadingType.SOLID, color: 'F8F8F8' }
              })
            ]
          }));
        });

        children.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows }));
        children.push(new Paragraph({ spacing: { after: 200 }, children: [] }));
      };

      if (isCvMode) {
        buildSection('Interview Questions (tailored to CV)', cvQuestionList, 1);
      } else {
        buildSection('Core Interview Questions', CORE_QUESTIONS, 1);
        buildSection('Additional HVAC / OME Questions', HVAC_QUESTIONS, 19);
        buildSection('Closing Question', [CLOSING_QUESTION], 22);
      }

      // Summary section header
      children.push(new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [new TableRow({
          children: [new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: 'Interview Summary', bold: true, size: 18, color: 'FFFFFF', font: 'Calibri' })] })],
            shading: { type: ShadingType.SOLID, color: orangeHex },
            borders: noBorders
          })]
        })]
      }));

      const summaryFields = [
        { label: 'OVERALL IMPRESSION', value: summary.overall_impression },
        { label: 'KEY STRENGTHS', value: summary.key_strengths },
        { label: 'AREAS OF CONCERN', value: summary.areas_of_concern },
        { label: 'RECOMMENDATION', value: summary.recommendation }
      ];

      summaryFields.forEach(field => {
        children.push(new Paragraph({
          spacing: { before: 120 },
          children: [new TextRun({ text: field.label, bold: true, size: 16, color: orangeHex, font: 'Calibri' })]
        }));
        children.push(new Paragraph({
          spacing: { after: 80 },
          children: [new TextRun({ text: field.value || '\u2014', size: 16, color: '333333', font: 'Calibri' })]
        }));
      });

      // Total score
      children.push(new Paragraph({ spacing: { before: 200 }, children: [
        new TextRun({ text: `TOTAL SCORE: ${totalScore} / ${maxScore}`, bold: true, size: 20, color: orangeHex, font: 'Calibri' }),
        new TextRun({ text: `     QUESTIONS SCORED: ${questionsScored} / ${ALL_QUESTIONS.length}`, size: 18, color: '666666', font: 'Calibri' })
      ]}));

      const doc = new Document({
        sections: [{ children }]
      });

      const blob = await Packer.toBlob(doc);
      const fileName = candidateName
        ? `Interview_Notes_${candidateName.replace(/\s+/g, '_')}.docx`
        : 'Interview_Notes.docx';
      saveAs(blob, fileName);
      toast.success('Word document downloaded');
    } catch (err) {
      console.error('Word generation error:', err);
      toast.error('Failed to generate Word document');
    }
  };

  // ============ Print ============
  const handlePrint = () => {
    // Generate PDF then open for printing
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = 15;

    const addPageIfNeeded = (needed) => {
      if (y + needed > 275) { doc.addPage(); y = 15; }
    };

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(238, 114, 3);
    doc.text('Technical Internal Sales', margin, y);
    y += 7;
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('First Interview Questions', margin, y);
    y += 10;

    doc.setFillColor(238, 114, 3);
    doc.rect(margin, y, contentW, 8, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(`CANDIDATE: ${candidateName || '\u2014'}`, margin + 3, y + 5.5);
    doc.text(`DATE: ${formatDisplayDate(interviewDate)}`, margin + contentW * 0.4, y + 5.5);
    doc.text(`INTERVIEWER: ${interviewer || '\u2014'}`, margin + contentW * 0.7, y + 5.5);
    y += 12;

    doc.setFillColor(245, 245, 245);
    doc.rect(margin, y, contentW, 7, 'F');
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('1 = Weak/vague    2 = Okay but limited    3 = Solid    4 = Very good    5 = Excellent/proven example', margin + 3, y + 4.5);
    y += 11;

    const renderSection = (title, qList, startNum) => {
      addPageIfNeeded(12);
      doc.setFillColor(238, 114, 3);
      doc.rect(margin, y, contentW, 7, 'F');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin + 3, y + 5);
      y += 10;

      qList.forEach((_, idx) => {
        const num = startNum + idx;
        const question = getQuestion(num);
        const notes = questions[num]?.notes || '';
        const score = questions[num]?.score || 0;
        doc.setFontSize(8);
        const qLines = doc.splitTextToSize(question, contentW * 0.3 - 4);
        const nLines = notes ? doc.splitTextToSize(notes, contentW * 0.4 - 4) : [''];
        const rowH = Math.max(qLines.length * 4 + 6, nLines.length * 4 + 6, 14);
        addPageIfNeeded(rowH + 2);

        doc.setFillColor(idx % 2 === 0 ? 255 : 248, idx % 2 === 0 ? 255 : 248, idx % 2 === 0 ? 255 : 248);
        doc.rect(margin, y, contentW, rowH, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.rect(margin, y, contentW, rowH, 'S');

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(238, 114, 3);
        doc.text(String(num), margin + 4, y + 5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        doc.text(qLines, margin + 12, y + 5);
        doc.setTextColor(80, 80, 80);
        doc.text(nLines, margin + contentW * 0.42, y + 5);

        const scoreX = margin + contentW * 0.85;
        for (let s = 1; s <= 5; s++) {
          const cx = scoreX + (s - 1) * 6;
          const cy = y + rowH / 2;
          if (s === score) {
            doc.setFillColor(238, 114, 3);
            doc.circle(cx, cy, 2.5, 'F');
            doc.setTextColor(255, 255, 255);
          } else {
            doc.setDrawColor(180, 180, 180);
            doc.circle(cx, cy, 2.5, 'S');
            doc.setTextColor(150, 150, 150);
          }
          doc.setFontSize(6);
          doc.setFont('helvetica', 'bold');
          doc.text(String(s), cx - 1, cy + 1);
        }
        y += rowH;
      });
    };

    if (isCvMode) {
      renderSection('Interview Questions (tailored to CV)', cvQuestionList, 1);
      y += 8;
    } else {
      renderSection('Core Interview Questions', CORE_QUESTIONS, 1);
      y += 4;
      renderSection('Additional HVAC / OME Questions', HVAC_QUESTIONS, 19);
      y += 4;
      renderSection('Closing Question', [CLOSING_QUESTION], 22);
      y += 8;
    }

    addPageIfNeeded(60);
    doc.setFillColor(238, 114, 3);
    doc.rect(margin, y, contentW, 7, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('Interview Summary', margin + 3, y + 5);
    y += 10;

    [
      { label: 'OVERALL IMPRESSION', value: summary.overall_impression },
      { label: 'KEY STRENGTHS', value: summary.key_strengths },
      { label: 'AREAS OF CONCERN', value: summary.areas_of_concern },
      { label: 'RECOMMENDATION', value: summary.recommendation }
    ].forEach(field => {
      addPageIfNeeded(20);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(238, 114, 3);
      doc.text(field.label, margin, y + 4);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const lines = doc.splitTextToSize(field.value || '\u2014', contentW - 4);
      const boxH = Math.max(lines.length * 4 + 4, 10);
      doc.setFillColor(250, 250, 250);
      doc.rect(margin, y, contentW, boxH, 'F');
      doc.setDrawColor(220, 220, 220);
      doc.rect(margin, y, contentW, boxH, 'S');
      doc.text(lines, margin + 2, y + 4);
      y += boxH + 4;
    });

    addPageIfNeeded(16);
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, y, contentW, 12, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(238, 114, 3);
    doc.text(`TOTAL SCORE: ${totalScore} / ${maxScore}`, margin + 4, y + 5);
    doc.setTextColor(100, 100, 100);
    doc.text(`QUESTIONS SCORED: ${questionsScored} / ${ALL_QUESTIONS.length}`, margin + 4, y + 10);

    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    window.open(blobUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 overflow-y-auto" data-testid="interview-notes-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1200px] my-8 mx-4">
        {/* Modal Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 rounded-t-xl px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold" style={{ color: enconOrange }} data-testid="modal-title">
              Technical Internal Sales
            </h2>
            <p className="text-sm text-gray-500">First Interview Questions</p>
            <p className={`text-[10px] uppercase tracking-wider mt-1 font-semibold ${
              autosaveStatus === 'error' ? 'text-red-600'
                : autosaveStatus === 'saving' ? 'text-amber-600'
                : autosaveStatus === 'saved' ? 'text-emerald-600'
                : 'text-gray-300'
            }`} data-testid="autosave-status">
              {autosaveStatus === 'saving' ? 'Saving…'
                : autosaveStatus === 'saved' ? 'All changes saved'
                : autosaveStatus === 'error' ? 'Autosave failed — your edits will be lost on close'
                : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={resetForm} variant="outline" size="sm" className="rounded-full text-gray-500" data-testid="modal-reset-btn">
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>
            <Button onClick={handlePrint} variant="outline" size="sm" className="rounded-full text-gray-500" data-testid="modal-print-btn">
              <Printer className="w-3 h-3 mr-1" /> Print
            </Button>
            <Button onClick={generatePDF} size="sm" className="rounded-full text-white font-semibold" style={{ backgroundColor: enconOrange }} data-testid="modal-pdf-btn">
              <FileDown className="w-3 h-3 mr-1" /> PDF
            </Button>
            <Button onClick={generateWord} size="sm" className="rounded-full text-white font-semibold" style={{ backgroundColor: '#2563eb' }} data-testid="modal-word-btn">
              <FileText className="w-3 h-3 mr-1" /> Word
            </Button>
            <button onClick={onClose} className="ml-2 p-1.5 rounded-full hover:bg-gray-100 transition-colors" data-testid="modal-close-btn">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4">
          {/* Candidate Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 rounded-xl p-4" data-testid="candidate-info">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Candidate Name</label>
              <Input value={candidateName} onChange={e => setCandidateName(e.target.value)} placeholder="Enter candidate name..." className="border-gray-300 focus:border-orange-400" data-testid="candidate-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Date</label>
              <Input type="date" value={interviewDate} onChange={e => setInterviewDate(e.target.value)} className="border-gray-300 focus:border-orange-400" data-testid="interview-date-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Interviewer</label>
              <Input value={interviewer} onChange={e => setInterviewer(e.target.value)} placeholder="Enter interviewer name..." className="border-gray-300 focus:border-orange-400" data-testid="interviewer-input" />
            </div>
          </div>

          {/* Scoring Guide */}
          <div className="rounded-xl overflow-hidden" data-testid="scoring-guide">
            <div className="px-4 py-2 text-white text-sm font-bold" style={{ backgroundColor: enconOrange }}>Scoring Guide</div>
            <div className="bg-white border border-t-0 border-gray-200 px-4 py-2 flex flex-wrap gap-4">
              {SCORING_GUIDE.map(g => (
                <span key={g.score} className="text-sm text-gray-600">
                  <span className="font-bold" style={{ color: enconOrange }}>{g.score}</span> = {g.label}
                </span>
              ))}
            </div>
          </div>

          {/* Core Interview Questions */}
          {isCvMode ? (
            <>
              <QuestionSection
                title="Interview Questions (tailored to CV)"
                questionList={cvQuestionList}
                startNum={1}
                questions={questions}
                updateQuestion={updateQuestion}
                customQuestions={customQuestions}
                updateQuestionText={updateQuestionText}
                editable={true}
                onMove={moveQuestion}
                onDelete={deleteQuestion}
              />
              <button
                type="button"
                onClick={() => addQuestion()}
                className="w-full rounded-xl border-2 border-dashed border-orange-300 bg-orange-50/40 hover:bg-orange-50 hover:border-orange-400 text-orange-700 font-semibold text-sm py-3 transition-colors"
                data-testid="add-question-btn"
              >
                + Add another question
              </button>
            </>
          ) : (
            <>
              <QuestionSection title="Core Interview Questions" questionList={CORE_QUESTIONS} startNum={1} questions={questions} updateQuestion={updateQuestion} customQuestions={customQuestions} updateQuestionText={updateQuestionText} />
              <QuestionSection title="Additional HVAC / OME Questions" questionList={HVAC_QUESTIONS} startNum={19} questions={questions} updateQuestion={updateQuestion} customQuestions={customQuestions} updateQuestionText={updateQuestionText} />
              <QuestionSection title="Closing Question" questionList={[CLOSING_QUESTION]} startNum={22} questions={questions} updateQuestion={updateQuestion} customQuestions={customQuestions} updateQuestionText={updateQuestionText} />
            </>
          )}

          {/* Interview Summary */}
          <div className="rounded-xl overflow-hidden" data-testid="interview-summary">
            <div className="px-4 py-2 text-white text-sm font-bold" style={{ backgroundColor: enconOrange }}>Interview Summary</div>
            <div className="bg-white border border-t-0 border-gray-200 p-4 space-y-4">
              <SummaryField label="Overall Impression" value={summary.overall_impression} onChange={v => setSummary(s => ({ ...s, overall_impression: v }))} testId="summary-overall" />
              <SummaryField label="Key Strengths" value={summary.key_strengths} onChange={v => setSummary(s => ({ ...s, key_strengths: v }))} testId="summary-strengths" />
              <SummaryField label="Areas of Concern" value={summary.areas_of_concern} onChange={v => setSummary(s => ({ ...s, areas_of_concern: v }))} testId="summary-concerns" />
              <SummaryField label="Recommendation" value={summary.recommendation} onChange={v => setSummary(s => ({ ...s, recommendation: v }))} testId="summary-recommendation" />
            </div>
          </div>

          {/* Total Score */}
          <div className="bg-gray-50 rounded-xl p-4 flex flex-wrap items-center justify-between" data-testid="total-score">
            <div>
              <span className="text-sm font-semibold text-gray-500 uppercase">Total Score</span>
              <p className="text-3xl font-bold" style={{ color: enconOrange }}>
                {totalScore} <span className="text-lg text-gray-400">/ {maxScore}</span>
              </p>
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-500 uppercase">Questions Scored</span>
              <p className="text-3xl font-bold text-gray-700">
                {questionsScored} <span className="text-lg text-gray-400">/ {activeQuestionCount}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-component for question sections.
// When `editable=true` (CV mode / parsed-from-PDF mode), the number badge
// becomes a number input that lets you type a new position to swap rows,
// and a trash icon appears so the user can remove individual questions.
const QuestionSection = ({ title, questionList, startNum, questions, updateQuestion, customQuestions, updateQuestionText, editable = false, onMove, onDelete }) => {
  const [editingQ, setEditingQ] = React.useState(null);
  const [posDraft, setPosDraft] = React.useState({});

  const commitPos = (currentNum) => {
    const raw = posDraft[currentNum];
    if (raw === undefined || raw === '') return;
    const target = parseInt(raw, 10);
    setPosDraft(prev => { const n = { ...prev }; delete n[currentNum]; return n; });
    if (Number.isFinite(target) && target !== currentNum) {
      onMove?.(currentNum, target);
    }
  };

  return (
    <div className="rounded-xl overflow-hidden" data-testid={`section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="px-4 py-2 text-white text-sm font-bold" style={{ backgroundColor: enconOrange }}>{title}</div>
      <div className="bg-white border border-t-0 border-gray-200 divide-y divide-gray-100">
        {questionList.map((_, idx) => {
          const num = startNum + idx;
          const questionText = customQuestions[num] || questionList[idx];
          const isEditing = editingQ === num;
          const draft = posDraft[num];
          return (
            <div key={num} className={`px-4 py-4 ${idx % 2 === 1 ? 'bg-gray-50/40' : ''}`} data-testid={`question-row-${num}`}>
              {/* Full-width question row */}
              <div className="flex items-start gap-2 mb-3">
                <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-0.5">
                  {editable ? (
                    <input
                      type="number"
                      min={1}
                      max={questionList.length}
                      value={draft !== undefined ? draft : num}
                      onChange={(e) => setPosDraft(prev => ({ ...prev, [num]: e.target.value }))}
                      onBlur={() => commitPos(num)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
                      className="w-9 h-7 rounded-full text-center text-white text-xs font-bold flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-orange-300"
                      style={{ backgroundColor: enconOrange, appearance: 'textfield' }}
                      title="Type a new position number to move this question"
                      data-testid={`question-pos-input-${num}`}
                    />
                  ) : (
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: enconOrange }}>{num}</span>
                  )}
                  <button
                    onClick={() => setEditingQ(isEditing ? null : num)}
                    className={`w-6 h-6 rounded flex items-center justify-center transition-all ${isEditing ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                    title={isEditing ? 'Done editing' : 'Edit question'}
                    data-testid={`edit-question-btn-${num}`}
                  >
                    {isEditing ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3 h-3" />}
                  </button>
                  {editable && (
                    <button
                      type="button"
                      onClick={() => onDelete?.(num)}
                      className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete question"
                      data-testid={`delete-question-btn-${num}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="flex-1 pt-0.5">
                  {isEditing ? (
                    <textarea
                      value={questionText}
                      onChange={e => updateQuestionText(num, e.target.value)}
                      className="w-full text-[15px] border-2 rounded-lg p-2 resize-none focus:outline-none min-h-[60px]"
                      style={{ borderColor: enconOrange }}
                      rows={3}
                      autoFocus
                      data-testid={`edit-question-input-${num}`}
                    />
                  ) : (
                    <div className="text-[15px] text-gray-700 leading-relaxed">{questionText}</div>
                  )}
                </div>
              </div>
              {/* Score (left) + Notes oblong (right) */}
              <div className="flex items-start gap-3 ml-10">
                <div className="flex items-center gap-1.5 flex-shrink-0 pt-1.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button
                      key={s}
                      onClick={() => updateQuestion(num, 'score', questions[num]?.score === s ? 0 : s)}
                      className={`w-8 h-8 rounded-full text-sm font-bold transition-all ${questions[num]?.score === s ? 'text-white shadow-md scale-110' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                      style={questions[num]?.score === s ? { backgroundColor: enconOrange } : {}}
                      data-testid={`question-score-${num}-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div className="flex-1">
                  <textarea
                    value={questions[num]?.notes || ''}
                    onChange={e => updateQuestion(num, 'notes', e.target.value)}
                    placeholder="Add notes..."
                    className="w-full text-sm border border-gray-200 rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-orange-300 min-h-[80px]"
                    rows={3}
                    data-testid={`question-notes-${num}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Sub-component for summary text areas
const SummaryField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-xs font-semibold uppercase mb-1 block" style={{ color: enconOrange }}>{label}</label>
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={`Enter ${label.toLowerCase()}...`} className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-1 focus:ring-orange-300 min-h-[80px]" rows={3} data-testid={testId} />
  </div>
);

export default InterviewNotesModal;
export { ALL_QUESTIONS, CORE_QUESTIONS, HVAC_QUESTIONS, CLOSING_QUESTION };
